import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { domainAPI } from '../lib/api';
import DomainCard from '../components/DomainCard';
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const CATEGORIES = ['all', 'tech', 'business', 'crypto', 'finance', 'health', 'media', 'gaming', 'ai'];
const TLDS = ['all', '.com', '.io', '.ai', '.net', '.org', '.co', '.dev', '.app'];
const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'name', label: 'Name A-Z' },
];

export default function DomainsPage() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [domains, setDomains] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState('all');
  const [tld, setTld] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort };
      if (search) {
        params.search = search;
      } else {
        params.is_auction = 'false';
      }
      if (category !== 'all') params.category = category;
      if (tld !== 'all') params.tld = tld;
      const res = await domainAPI.list(params);
      setDomains(res.data.domains || []);
      setTotal(res.data.total || 0);
    } catch { setDomains([]); }
    setLoading(false);
  };

  useEffect(() => { fetchDomains(); }, [category, tld, sort, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDomains();
  };

  const clearFilters = () => {
    setSearch('');
    setCategory('all');
    setTld('all');
    setSort('newest');
    setPage(1);
  };

  const hasActiveFilters = category !== 'all' || tld !== 'all' || search;

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="domains-page">
      <div className="mb-10">
        <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('domains.title')}</h1>
        <p className="text-zinc-500 mt-2">Find your perfect domain from our curated marketplace</p>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('hero.search')}
              className="pl-10 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="domains-search-input" />
          </div>
          <Button type="submit" className="h-11 px-5 bg-[#00E5FF] text-black hover:bg-white font-bold rounded-sm" data-testid="domains-search-btn">
            {t('common.search')}
          </Button>
        </form>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}
            className="h-11 border-zinc-800 text-zinc-400 hover:border-[#00E5FF] hover:text-[#00E5FF] rounded-sm" data-testid="filters-toggle">
            <SlidersHorizontal className="h-4 w-4 mr-2" /> {t('domains.filters')}
          </Button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 w-48 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              {SORTS.map(s => (
                <SelectItem key={s.value} value={s.value} className="text-zinc-300 focus:text-[#00E5FF] focus:bg-white/5">{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-8 p-6 bg-[#0A0A0A] border border-white/5 rounded-sm animate-fade-in" data-testid="filter-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-3 block">{t('domains.category')}</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => (
                  <button key={c} onClick={() => { setCategory(c); setPage(1); }}
                    className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-sm border transition-colors ${category === c ? 'border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/5' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}
                    data-testid={`filter-cat-${c}`}>
                    {c === 'all' ? t('common.all') : c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-3 block">{t('domains.tld')}</label>
              <div className="flex flex-wrap gap-2">
                {TLDS.map(ext => (
                  <button key={ext} onClick={() => { setTld(ext); setPage(1); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${tld === ext ? 'border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/5' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}
                    data-testid={`filter-tld-${ext}`}>
                    {ext === 'all' ? t('common.all') : ext}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="mt-4 flex items-center gap-1 text-xs text-zinc-500 hover:text-red-400 transition-colors">
              <X className="h-3 w-3" /> Clear filters
            </button>
          )}
        </div>
      )}

      {/* Active Filters */}
      {hasActiveFilters && !showFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {search && <Badge variant="outline" className="border-[#00E5FF]/30 text-[#00E5FF] text-xs">Search: {search}</Badge>}
          {category !== 'all' && <Badge variant="outline" className="border-[#00E5FF]/30 text-[#00E5FF] text-xs">{category}</Badge>}
          {tld !== 'all' && <Badge variant="outline" className="border-[#00E5FF]/30 text-[#00E5FF] text-xs">{tld}</Badge>}
          <button onClick={clearFilters} className="text-xs text-zinc-500 hover:text-red-400"><X className="h-3 w-3 inline" /> Clear</button>
        </div>
      )}

      {/* Results */}
      <div className="mb-4 text-xs text-zinc-600 uppercase tracking-wider">
        {total} domain{total !== 1 ? 's' : ''} found
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>
      ) : domains.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-zinc-500">{t('domains.noResults')}</p>
          <Button onClick={clearFilters} variant="outline" className="mt-4 border-zinc-800 text-zinc-400 rounded-sm">Clear filters</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="domains-grid">
          {domains.map(d => <DomainCard key={d.id} domain={d} />)}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2 mt-12">
          <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="border-zinc-800 text-zinc-400 rounded-sm">{t('common.prev')}</Button>
          <span className="px-4 py-2 text-sm text-zinc-500">Page {page}</span>
          <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={domains.length < 20}
            className="border-zinc-800 text-zinc-400 rounded-sm">{t('common.next')}</Button>
        </div>
      )}
    </div>
  );
}
